import React, { useState, useEffect, useContext ,useMemo, useRef} from 'react';
import './styles/ViewRequest.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Tabs from '../components/Tabs/Tabs';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { notifyError, notifySuccess, ToastContainer } from '../components/ErrorAlert';
import { NotifyMessages } from '../components/NotifyMessages';
import { FaPlus } from 'react-icons/fa';
import moment from 'moment';
import { IoMdSend } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { encryptId } from "../utils/encrypt";
import { canEditBookingDetailsForAdmin, isAdminBookingLockedByManager, shouldShowAdminBookingAction } from '../utils/approvalAccess';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RequestPopup from "../components/RequestPopup";
// Format date safely
const formatDate = (date) => {
  if (!date) return "NA";
  const temp = date.split('T')[0];
  return moment(temp).format('DD-MM-YYYY');
};

const isSelfBookedTravelAccommodation = (request) =>
  ["Travel", "Accommodation", "Travel + Accommodation"].includes(request?.supportNeeded) &&
  Boolean(request?.bookingDetails?.selfBooked);

const isExpenseRequest = (request) =>
  Boolean(request?.bookingDetails?.selfBooked);

const isCourierRequest = (request) =>
  Boolean(request?.supportNeeded && request.supportNeeded.includes("Courier"));

const hasDatePassed = (value) => {
  if (!value) return false;
  return moment(value).endOf("day").isBefore(moment());
};

const getApprovalRequestCompletionDate = (request) => {
  if (!request) return null;

  if (request.supportNeeded === "Accommodation" || request.supportNeeded === "Travel + Accommodation") {
    return request.toDate || request.fromDate || null;
  }

  if (request.supportNeeded === "Travel") {
    return request.fromDate || null;
  }

  if (isCourierRequest(request)) {
    return request.receivedOn || request?.bookingDetails?.bookingDate || null;
  }

  return null;
};

const isBookingDetailsComplete = (request) => {
  const details = request?.bookingDetails || {};
  if (isCourierRequest(request)) {
    return Boolean(
      details.bookingDate &&
      details.bookingBy &&
      details.amount &&
      details.podNumber &&
      details.courierCo &&
      details.fileUrl
    );
  }

  return Boolean(
    details.bookingDate &&
    details.bookingBy &&
    details.amount &&
    details.pnrNumber &&
    details.fileUrl
  );
};

const getWorkflowStatusLabel = () => "Booking Status";

const getWorkflowStatusValue = (request) => {
  return request?.status || "Requested";
};

const getCardStatusValue = (request) => {
  const status = String(request?.status || "").toLowerCase();

  if (status === "requested") {
    return "Pending";
  }

  if (status === "declined") {
    return "Rejected";
  }

  if (status === "booked") {
    return "Approved";
  }

  return request?.status || "Pending";
};

const getCardSecondaryStatusLabel = (request) =>
  isSelfBookedTravelAccommodation(request)
    ? "Reimbursement Status"
    : "Booking Status";

const getCardSecondaryStatusValue = (request) => {
  if (isSelfBookedTravelAccommodation(request)) {
    const reimbursementStatus = String(request?.bookingDetails?.reimbursementStatus || "").toLowerCase();

    if (reimbursementStatus === "approved" || String(request?.status || "").toLowerCase() === "approved") {
      return "Approved";
    }

    if (reimbursementStatus === "rejected" || String(request?.status || "").toLowerCase() === "declined") {
      return "Rejected";
    }

    return request?.bookingDetails?.reimbursementStatus || "Pending";
  }

  const bookingStatus = String(request?.bookingDetails?.bookingStatus || "").toLowerCase();
  const requestStatus = String(request?.status || "").toLowerCase();

  if (bookingStatus === "success" || requestStatus === "booked") {
    return "Success";
  }

  if (bookingStatus === "fail" || requestStatus === "declined") {
    return "Fail";
  }

  if (!bookingStatus || bookingStatus === "pending") {
    return "Pending";
  }

  return request?.bookingDetails?.bookingStatus || "Pending";
};

const getPassengerNames = (request) =>
  request?.individuals?.map((individual) => individual.name).filter(Boolean).join(", ") || "NA";

const isRequestFinalizedForEdits = (request) => {
  if (isExpenseRequest(request)) {
    return request?.status === "Approved";
  }
  return request?.status === "Booked";
};

const isClosedRequest = (request) => {
  const status = String(request?.status || "").toLowerCase();
  const bookingStatus = String(
    request?.bookingDetails?.bookingStatus || ""
  ).toLowerCase();

  // Explicitly completed/terminal requests
  if (["cancelled", "declined", "rejected"].includes(status)) {
    return true;
  }

  // Self-booked / expense requests
  if (isExpenseRequest(request)) {
    return status === "approved";
  }

  // Successful booking means request is closed
  if (bookingStatus === "success") {
    return true;
  }

  return false;
};

const isAdministrationUser = (user) => {
  const department = String(user?.department || "").toLowerCase();
  const approvalRole = String(user?.approvalRole || "").toUpperCase();

  return department.includes("administration") || approvalRole === "ADMIN_TEAM";
};

const canApproveCurrentStage = () => false;

const canManageBooking = () => false;

const renderBookingDetailsAction = (request) => {
  if (!shouldShowAdminBookingAction(request)) {
    return null;
  }

  if (canEditBookingDetailsForAdmin(request)) {
    return (
      <div className="booking-details">
        <Link to={`/booking-details/${encryptId(request.id)}`}>
          Update/Edit Booking Details
        </Link>
      </div>
    );
  }

  if (isAdminBookingLockedByManager(request)) {
    return (
      <div className="booking-details">
        <button type="button" className="reqbtn" disabled>
          Update/Edit Booking Details
        </button>
      </div>
    );
  }

  return null;
};

const ViewEditRequests = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(UserContext);

  const [myOpenRequests, setMyOpenRequests] = useState([]);
  const [myClosedRequests, setMyClosedRequests] = useState([]);
  const [allOpenRequests, setAllOpenRequests] = useState([]);
  const [allClosedRequests, setAllClosedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const filterRef = useRef(null);
  const popupRef = useRef(null);
  const [remarksMap, setRemarksMap] = useState({});
  const [selected, setSelected] = useState({});

  const [showFilters, setShowFilters] = useState(false);

  const [filterOptions, setFilterOptions] = useState({
  supportType: [],
  requestStatus: [],
  bookingStatus: [],
});

  const [dropdownVisible, setDropdownVisible] = useState({
  supportType: false,
  requestStatus: false,
  bookingStatus: false,
 });

const [reqSearchHeader, setReqSearchHeader] = useState("batchId");
const [reqSearchTerm, setReqSearchTerm] = useState("");
const [reqDateRange, setReqDateRange] = useState({
  start: null,
  end: null,
});

const applySearch = (list) => {
  let filtered = [...list];

  // TEXT SEARCH
  if (reqSearchTerm && reqSearchHeader !== "requestDate") {
    filtered = filtered.filter((req) => {
      let value = "";

      switch (reqSearchHeader) {
        case "batchId":
          value = req.batchId;
          break;
        case "supportNeeded":
          value = req.supportNeeded;
          break;
        case "officeLocation":
          value = req.officeLocation;
          break;
        case "status":
          value = req.status;
          break;
        case "bookingStatus":
          value = getWorkflowStatusValue(req);
          break;
        case "podNumber":
          value = req.bookingDetails?.podNumber;
          break;
          case "requesterName":
          value = req.user?.name;
          break;

          case "center":
    value = req.center;
    break;

  case "client":
    value = req.client;
    break;

  case "costCenter":
    value = req.costCenter;
    break;

  case "source":
    value = req.source;
    break;

  case "destination":
    value = req.destination;
    break;

        default:
          value = "";
      }

      return (
        value &&
        String(value).toLowerCase().includes(reqSearchTerm.toLowerCase())
      );
    });
  }

  // DATE RANGE SEARCH
  if (
    reqSearchHeader === "requestDate" &&
    reqDateRange.start &&
    reqDateRange.end
  ) {
    filtered = filtered.filter((req) => {
      const created = moment(req.createdAt);
      return created.isBetween(
        moment(reqDateRange.start),
        moment(reqDateRange.end),
        null,
        "[]"
      );
    });
  }

  return filtered;
};

  useEffect(() => {
    if (user) {
      fetchMyRequests();
      if (user.role !== "USER") {
        fetchAllRequests();
      }
    }
  }, [user, token]);

  useEffect(() => {
  const handleEsc = (e) => {
    if (e.key === "Escape") setSelected({});
  };

  document.addEventListener("keydown", handleEsc);
  return () => document.removeEventListener("keydown", handleEsc);
}, []);

useEffect(() => {
  const handleClickOutside = (event) => {
    // 1. Logic for Filters (Existing)
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setShowFilters(false);
      setDropdownVisible({ supportType: false, requestStatus: false, bookingStatus: false });
    }

    // 2. Logic for Request Popup (NEW)
    // If popupRef exists and the click was NOT inside the popupRef, close it.
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setSelected({});
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [selected]); // Dependency on selected ensures it checks correctly when open

useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(event.target)
    ) {
      setShowFilters(false); // ✅ CORRECT
      setDropdownVisible({
        supportType: false,
        requestStatus: false,
        bookingStatus: false,
      });
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

  const fetchMyRequests = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/requests/all/${user.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(err);
      }

      const json = await response.json();
      categorizeMyRequests(json);
      initializeRemarks(json);
    } catch (err) {
      console.error("Error fetching my requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRequests = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/requests/all`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(err);
      }

      const json = await response.json();
      categorizeAllRequests(json);
      initializeRemarks(json);
    } catch (err) {
      console.error("Error fetching all requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const categorizeMyRequests = (requests) => {
    const open = [];
    const closed = [];

    requests.forEach((req) => {
      isClosedRequest(req) ? closed.push(req) : open.push(req);
    });

    open.sort((a, b) => a.id - b.id);
    closed.sort((a, b) => a.id - b.id);

    setMyOpenRequests(open);
    setMyClosedRequests(closed);
  };

  const categorizeAllRequests = (requests) => {
    const open = [];
    const closed = [];

    requests.forEach((req) => {
      isClosedRequest(req) ? closed.push(req) : open.push(req);
    });

    open.sort((a, b) => a.id - b.id);
    closed.sort((a, b) => a.id - b.id);

    setAllOpenRequests(open);
    setAllClosedRequests(closed);
  };

  const initializeRemarks = (requests) => {
    const map = {};
    requests.forEach((r) => {
      map[r.id] = r.adminRemarks || [];
    });
    setRemarksMap(map);
  };

const toggleDropdown = (category) => {
  setDropdownVisible(prev => {
    const next = { ...prev, [category]: !prev[category] };
    Object.keys(next).forEach(k => {
      if (k !== category) next[k] = false;
    });
    return next;
  });
};

const handleFilterChange = (category, value) => {
  setFilterOptions(prev => {
    const next = { ...prev };
    if (next[category].includes(value)) {
      next[category] = next[category].filter(v => v !== value);
    } else {
      next[category].push(value);
    }
    return next;
  });
};

const clearFilters = () => {
  setFilterOptions({
    supportType: [],
    requestStatus: [],
    bookingStatus: [],
  });

  setReqSearchTerm("");
  setReqDateRange({ start: null, end: null });
  setReqSearchHeader("batchId");

  setDropdownVisible({
    supportType: false,
    requestStatus: false,
    bookingStatus: false,
  });

  setShowFilters(false);
};

const applyFilters = (list) => {
  let filtered = [...list];

  if (filterOptions.supportType.length > 0) {
    filtered = filtered.filter(r =>
      filterOptions.supportType.includes(r.supportNeeded)
    );
  }

  if (filterOptions.requestStatus.length > 0) {
    filtered = filtered.filter(r =>
      filterOptions.requestStatus.includes(r.status || "Requested")
    );
  }

  if (filterOptions.bookingStatus.length > 0) {
    filtered = filtered.filter(r =>
      filterOptions.bookingStatus.includes(
        getWorkflowStatusValue(r)
      )
    );
  }

  return filtered;
};
  const handleDeleteRequest = async (requestId) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error("Failed to delete request.");

    setMyOpenRequests(p => p.filter(r => r.id !== requestId));
    setMyClosedRequests(p => p.filter(r => r.id !== requestId));

    notifySuccess(NotifyMessages.requestDeleted, "bottom-center");

  } catch (err) {
    console.error("Delete API error:", err);
    notifyError("Unable to delete request", "bottom-center");
  }
};

  const handleAcceptRequest = async (requestId) => {
    try {
      const remarks = (remarksMap[requestId] || []).map((r) => (r || "").trim()).filter(Boolean);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}/accept`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ remarks: remarks[0] || "" }),
      });

      if (!res.ok) throw new Error("Failed to accept.");

      const updated = await res.json();

      setMyOpenRequests((prev) => {
        const updatedOpen = prev.filter((r) => r.id !== requestId);

        return updatedOpen;
      });

      setMyClosedRequests((prev) => {
        const alreadyExists = prev.some((r) => r.id === requestId);

        return alreadyExists
          ? prev.map((r) => (r.id === requestId ? updated : r))
          : [...prev, updated].sort((a, b) => a.id - b.id);
      });

      setAllOpenRequests((prev) => {
        const updatedOpen = prev.filter((r) => r.id !== requestId);

        return updatedOpen;
      });

      setAllClosedRequests((prev) => {
        const alreadyExists = prev.some((r) => r.id === requestId);

        return alreadyExists
          ? prev.map((r) => (r.id === requestId ? updated : r))
          : [...prev, updated].sort((a, b) => a.id - b.id);
      });
      notifySuccess(NotifyMessages.requestAccepted, "bottom-center");

      if (!updated.supportNeeded.includes("Courier")) {
        setTimeout(() => navigate(`/planner/${requestId}`), 1500);
      }
    } catch (err) {
      console.error("Accept error:", err);
    }
  };
  const handleDenyRequest = async (requestId) => {
    try {
      const remarks = (remarksMap[requestId] || []).map((r) => (r || "").trim()).filter(Boolean);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}/deny`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ remarks: remarks[0] || "" }),
      });

      if (!res.ok) throw new Error("Failed to deny.");

      const updated = await res.json();

      setMyOpenRequests((p) => p.map((r) => (r.id === requestId ? updated : r)));
      setAllOpenRequests((p) => p.map((r) => (r.id === requestId ? updated : r)));

      notifySuccess(NotifyMessages.requestDenied, "bottom-center");
    } catch (err) {
      console.error("Deny error:", err);
    }
  };

  const handleSuccess = async (requestId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}/booking-status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bookingStatus: "Success" }),
      });
      if (!res.ok) throw new Error("Failed to update booking status.");

      const updated = await res.json();

      setMyOpenRequests((p) => p.map((r) => (r.id === requestId ? updated : r)));
      setAllOpenRequests((p) => p.map((r) => (r.id === requestId ? updated : r)));

      notifySuccess(NotifyMessages.bookingStatusSuccess, "bottom-center");
    } catch (err) {
      console.error("Booking success error:", err);
    }
  };


  const handleFail = async (requestId) => {
    const remarks = (remarksMap[requestId] || []).map((r) => (r || "").trim()).filter(Boolean);
    if (remarks.length === 0) {
      notifyError("Please add at least one remark before marking as Fail.", "bottom-center");
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}/booking-status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bookingStatus: "Fail" }),
      });

      if (!res.ok) throw new Error("Failed to update booking status.");

      const updated = await res.json();

      setMyOpenRequests((p) => p.map((r) => (r.id === requestId ? updated : r)));
      setAllOpenRequests((p) => p.map((r) => (r.id === requestId ? updated : r)));

      notifySuccess(NotifyMessages.bookingStatusFail, "bottom-center");
    } catch (err) {
      console.error("Booking fail error:", err);
    }
  };

  const handleChangeRemarks = (event, requestId, index) => {
    const { value } = event.target;

    setRemarksMap((prev) => {
      const current = [...(prev[requestId] || [])];
      current[index] = value;
      return { ...prev, [requestId]: current };
    });
  };

  const handleAddRemark = (requestId) => {
    setRemarksMap((prev) => ({
      ...prev,
      [requestId]: [...(prev[requestId] || []), ""],
    }));
  };

  const handleDeleteRemark = (requestId, index) => {
    setRemarksMap((prev) => {
      const arr = [...(prev[requestId] || [])];
      arr.splice(index, 1);
      return { ...prev, [requestId]: arr };
    });
  };

 const sendRemarks = async (requestId) => {
  const remarks = remarksMap[requestId] || [];

  // VALIDATION: remark must not be empty
  const hasValidRemark = remarks.some(r => r && r.trim() !== "");

  if (!hasValidRemark) {
    notifyError("Please enter at least one remark before sending.", "bottom-center");
    return;
  }

  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/sendRemarks/${requestId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ remarks }),
    });

    if (!res.ok) throw new Error("Failed to send remarks");

    notifySuccess(NotifyMessages.remarksSent, "bottom-center");
  } catch (err) {
    console.error("Send remarks error:", err);
    notifyError("Error sending remarks", "bottom-center");
  }
};


  const tabData = user?.role !== "USER"
    ? [{ label: "My Requests" }, { label: "All Requests" }, { label: "Inbox", to: "/inbox" }]
    : [{ label: "My Requests" }, { label: "Inbox", to: "/inbox" }];

  const subTabs = [{ label: "Open" }, { label: "Closed" }];

  const isSearchActive =
  reqSearchTerm.trim() !== "" ||
  (reqSearchHeader === "requestDate" &&
    reqDateRange.start &&
    reqDateRange.end);

const isFilterActive =
  filterOptions.supportType.length > 0 ||
  filterOptions.requestStatus.length > 0 ||
  filterOptions.bookingStatus.length > 0;

  const getVisibleRequests = (list) => {
  let result = [...list];
  if (isFilterActive) {
    result = applyFilters(result);
  }
  if (isSearchActive) {
    result = applySearch(result);
  }

  return result;
};

const renderJourneyDetails = (request) => {
  if (request.tripType === "Round Trip") {
    return (
      <>
        <div className="location">
          <strong>Going Journey:</strong>
        </div>

        <div className="location">
          <strong>Source:</strong>
          <span>{request.source || "NA"}</span>
        </div>

        <div className="location">
          <strong>Destination:</strong>
          <span>{request.destination || "NA"}</span>
        </div>

        <div>
          <strong>Date:</strong>
          <span>{formatDate(request.fromDate)}</span>
        </div>

        <div>
          <strong>Travel Preference:</strong>
          <span>{request.travelPreference || "NA"}</span>
        </div>

        <div>
          <strong>
            {request.travelPreference === "Train"
              ? "Train Number:"
              : request.travelPreference === "Flight"
              ? "Flight Number:"
              : "Bus Name:"}
          </strong>
          <span>
            {request.travelPreference === "Train"
              ? request.trainNumber || "NA"
              : request.travelPreference === "Bus"
              ? request.busName || "NA"
              : "NA"}
          </span>
        </div>

        <div>
          <strong>Time:</strong>
          <span>
            {request.travelPreference === "Train"
              ? request.trainTiming || "NA"
              : request.travelPreference === "Bus"
              ? request.busTiming || "NA"
              : "NA"}
          </span>
        </div>

        <div>
          <strong>Travel Class:</strong>
          <span>{request.travelClass || "NA"}</span>
        </div>

        <div className="location">
          <strong>Return Journey:</strong>
        </div>

        <div className="location">
          <strong>Source:</strong>
          <span>{request.returnSource || "NA"}</span>
        </div>

        <div className="location">
          <strong>Destination:</strong>
          <span>{request.returnDestination || "NA"}</span>
        </div>

        <div>
          <strong>Date:</strong>
          <span>{formatDate(request.returnDate)}</span>
        </div>

        <div>
          <strong>Travel Preference:</strong>
          <span>{request.returnTravelPreference || "NA"}</span>
        </div>

        <div>
          <strong>
            {request.returnTravelPreference === "Train"
              ? "Train Number:"
              : request.returnTravelPreference === "Flight"
              ? "Flight Number:"
              : "Bus Name:"}
          </strong>
          <span>
            {request.returnTravelPreference === "Train"
              ? request.returnTrainNumber || "NA"
              : request.returnTravelPreference === "Bus"
              ? request.returnBusName || "NA"
              : "NA"}
          </span>
        </div>

        <div>
          <strong>Time:</strong>
          <span>
            {request.returnTravelPreference === "Train"
              ? request.returnTrainTiming || "NA"
              : request.returnTravelPreference === "Bus"
              ? request.returnBusTiming || "NA"
              : "NA"}
          </span>
        </div>

        <div>
          <strong>Travel Class:</strong>
          <span>{request.returnTravelClass || "NA"}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="location">
        <strong>Source:</strong>
        <span>{request.source || "NA"}</span>
      </div>

      <div className="location">
        <strong>Destination:</strong>
        <span>{request.destination || "NA"}</span>
      </div>

      <div>
        <strong>Date:</strong>
        <span>
          {request.supportNeeded === "Travel"
            ? formatDate(request.fromDate)
            : `${formatDate(request.fromDate)} to ${formatDate(request.toDate)}`}
        </span>
      </div>

      <div>
        <strong>Travel Preference:</strong>
        <span>{request.travelPreference || "NA"}</span>
      </div>

      <div>
        <strong>
          {request.travelPreference === "Train"
            ? "Train Number:"
            : request.travelPreference === "Flight"
            ? "Flight Number:"
            : "Bus Name:"}
        </strong>
        <span>
          {request.travelPreference === "Train"
            ? request.trainNumber || "NA"
            : request.travelPreference === "Bus"
            ? request.busName || "NA"
            : "NA"}
        </span>
      </div>

      <div>
        <strong>Time:</strong>
        <span>
          {request.travelPreference === "Train"
            ? request.trainTiming || "NA"
            : request.travelPreference === "Bus"
            ? request.busTiming || "NA"
            : "NA"}
        </span>
      </div>

      <div>
        <strong>Travel Class:</strong>
        <span>{request.travelClass || "NA"}</span>
      </div>
    </>
  );
};

  return (
    <div className="ViewEditRequests">
      <Header />
      <ToastContainer />

      <main className="main-view">

     <Tabs
  tabs={tabData}
  rightContent={
    <div className="req-action-bar">

      <div className="req-search-section">

        {/* SEARCH BY */}
        <div className="req-search-by">
          <label>
            <strong>Search By:</strong>
            <select
              value={reqSearchHeader}
              onChange={(e) => setReqSearchHeader(e.target.value)}
            >
              <option value="batchId">Batch ID</option>
              <option value="supportNeeded">Support Needed</option>
              <option value="officeLocation">Office Location</option>
              <option value="status">Request Status</option>
              <option value="bookingStatus">Booking/Reimbursement Status</option>
              <option value="podNumber">POD Number</option>
              <option value="center">Center</option>
              <option value="client">Client</option>
              <option value="costCenter">Cost Center</option>
              <option value="source">Source</option>
              <option value="destination">Destination</option>
              <option value="requestDate">Request Date</option>
              <option value="requesterName">Requester Name</option>
            </select>
          </label>
        </div>

        {/* SEARCH HERE */}
        <div className="req-search-here">
          <label><strong>Search Here:</strong></label>

          {reqSearchHeader !== "requestDate" ? (
            <input
              type="text"
              placeholder="Search here..."
              value={reqSearchTerm}
              onChange={(e) => setReqSearchTerm(e.target.value)}
            />
          ) : (
            <DatePicker
              selectsRange
              startDate={reqDateRange.start}
              endDate={reqDateRange.end}
              onChange={(dates) =>
                setReqDateRange({ start: dates[0], end: dates[1] })
              }
              className="req-date-picker"
              placeholderText="Select date range"
            />
          )}
        </div>

      </div>
      <div className="req-filter-buttons" ref={filterRef}>
{showFilters && (
  <div className="vr-filter-wrapper">
    
    {/* Support Type */}
    <div className="dropdown">
      <button 
        className={`vr-filter-item ${dropdownVisible.supportType ? 'active' : ''}`}
        onClick={() => toggleDropdown("supportType")}
      >
        Support Type
      </button>
      {dropdownVisible.supportType && (
        <div className="vr-filter-options">
          {["Travel", "Accommodation","Travel + Accomodation", "Send Courier", "Receive Courier"].map(opt => (
            <label key={opt}>
              <input
                type="checkbox"
                checked={filterOptions.supportType.includes(opt)}
                onChange={() => handleFilterChange("supportType", opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>

    {/* Request Status */}
    <div className="dropdown">
      <button 
        className={`vr-filter-item ${dropdownVisible.requestStatus ? 'active' : ''}`}
        onClick={() => toggleDropdown("requestStatus")}
      >
        Request Status
      </button>
      {dropdownVisible.requestStatus && (
        <div className="vr-filter-options">
          {["Requested", "Approved", "Declined", "Booked", "Cancelled"].map(opt => (
            <label key={opt}>
              <input
                type="checkbox"
                checked={filterOptions.requestStatus.includes(opt)}
                onChange={() => handleFilterChange("requestStatus", opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>

    {/* Booking/Reimbursement Status */}
    <div className="dropdown">
      <button 
        className={`vr-filter-item ${dropdownVisible.bookingStatus ? 'active' : ''}`}
        onClick={() => toggleDropdown("bookingStatus")}
      >
        Booking/Reimbursement Status
      </button>
      {dropdownVisible.bookingStatus && (
        <div className="vr-filter-options">
          {["Requested", "Approved", "Declined", "Booked", "Cancelled"].map(opt => (
            <label key={opt}>
              <input
                type="checkbox"
                checked={filterOptions.bookingStatus.includes(opt)}
                onChange={() => handleFilterChange("bookingStatus", opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>

  </div>
)}

        <button className="req-btn" onClick={() => setShowFilters(p => !p)}>
          Filter
        </button>

        <button className="req-btn" onClick={clearFilters}>
          Clear
        </button>
      </div>

      <button
        className="create-request-button"
        onClick={() => navigate("/create-request")}
      >
        + Create Request
      </button>

    </div>
  }
>
          <div>
            <Tabs tabs={subTabs}>
              <div>
                {loading ? (
                  <p className="no-req">Loading...</p>
                ) : myOpenRequests.length === 0 ? (
                  <p className="no-req">No Requests Found</p>
                ) : (
                  <div className="requests-list">

                   {getVisibleRequests(myOpenRequests).map((request) => (
                      <div key={request.id}>
                        <div
                          className={`request-card ${selected?.id === request.id ? "selected" : ""}`}
                        >
                          <div className="card-header">
                            <h3>Request ID: {request.id}</h3>
                            <button className="show-more-btn" onClick={() => setSelected(request)}>
                              Show more
                            </button>
                          </div>

                          <div className="card-body">
                            <div><strong>Support Needed:</strong><span>{request.supportNeeded}</span></div>
                            <div><strong>Status:</strong><span>{getCardStatusValue(request)}</span></div>
                            <div><strong>{getCardSecondaryStatusLabel(request)}:</strong>
                              <span>{getCardSecondaryStatusValue(request)}</span>
                            </div>
                            <div className="passengers-row">
                              <strong>Passengers:</strong>
                              <span className="passenger-names">
                                {getPassengerNames(request)
                                  .split(",")
                                  .map((name, index) => (
                                    <span key={index}>{name.trim()}</span>
                                  ))}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* POPUP DETAILS */}

                        {selected?.id === request.id && (
                          <RequestPopup
                            request={request}
                            popupRef={popupRef}
                            onClose={() => setSelected({})}
                            encryptId={encryptId}
                          >

                              
                              {request.supportNeeded.includes("Courier") ? (
                                <>
                                  <div><strong>Support Needed:</strong><span>{request.supportNeeded}</span></div>
                                  <div><strong>Courier Type:</strong><span>{request.courierType}</span></div>
                                  <div><strong>Office Location:</strong><span>{request.officeLocation}</span></div>

                                  <div className="location">
                                    <strong>Source Address:</strong>
                                    <span>{request.fromLocation || "NA"}</span>
                                  </div>

                                  <div className="location">
                                    <strong>Destination Address:</strong>
                                    <span>{request.toLocation || "NA"}</span>
                                  </div>
                                  <div className="popup-field">
                                  <strong>Requester Name:</strong>
                                  <span>{request.user?.name || "NA"}</span>
                                  </div>
                                  <div><strong>POD Number:</strong>
                                    <span>{request.bookingDetails?.podNumber || "NA"}</span>
                                  </div>

                                  <div><strong>Received Status:</strong>
                                    <span>{request.receivedStatus ? "Yes" : "No"}</span>
                                  </div>

                                  <div><strong>Received On:</strong>
                                    <span>{request.receivedOn ? formatDate(request.receivedOn) : "NA"}</span>
                                  </div>

                                  <div><strong>Status:</strong>
                                    <span>{request.status || "Pending"}</span>
                                  </div>

                                  <div><strong>{getWorkflowStatusLabel(request)}:</strong>
                                    <span>{getWorkflowStatusValue(request)}</span>
                                  </div>

                                  {/* Cancel request */}
                                  {request.status !== "Cancelled" && (
                                    <div>
                                   <Link to={`/cancel-request/${request.id}`} className="full-details">Cancel Request
                                  </Link>
                                    </div>
                                  )}

                                  {/* Edit/Delete */}
                                  {!isRequestFinalizedForEdits(request) && (
                                    <div className="request-button">
                                      <button className="reqbtn" onClick={() => navigate(`/create-request/${request.id}`)}>
                                        Edit
                                      </button>
                                      <button className="reqbtn" onClick={() => handleDeleteRequest(request.id)}>
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                
                                  <div><strong>Support Needed:</strong><span>{request.supportNeeded}</span></div>
                                  <div ><strong>Requester Name:</strong><span>{request.user?.name || "NA"}</span> </div>
                                  <div><strong>Batch Id:</strong><span>{request.batchId}</span></div>
                                  <div><strong>Center:</strong><span>{request.center}</span></div>
                                  <div><strong>Client:</strong><span>{request.client}</span></div>
                                  <div><strong>Cost Center:</strong><span>{request.costCenter}</span></div>

                                  {renderJourneyDetails(request)}
                                  


                                  <div><strong>Status:</strong><span>{request.status}</span></div>

                                  <div><strong>{getWorkflowStatusLabel(request)}:</strong>
                                    <span>{getWorkflowStatusValue(request)}</span>
                                  </div>

                                  {/* Cancel */}
                                  {request.status !== "Cancelled" && (
                                    <div>
                                      <Link to={`/cancel-request/${request.id}`} className="full-details">
                                      Cancel Request
                                   </Link>
                                    </div>
                                  )}

                                  {/* Edit/Delete */}
                                  {!isRequestFinalizedForEdits(request) && (
                                    <div className="request-button">
                                      <button className="reqbtn" onClick={() => navigate(`/create-request/${request.id}`)}>
                                        Edit
                                      </button>

                                      <button className="reqbtn" onClick={() => handleDeleteRequest(request.id)}>
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                          </RequestPopup>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                {loading ? (
                  <p className="no-req">Loading...</p>
                ) : myClosedRequests.length === 0 ? (
                  <p className="no-req">No Requests Found</p>
                ) : (
                  <div className="requests-list">

                    {getVisibleRequests(myClosedRequests).map((request) => (
                      <div key={request.id}>

                        {/* CARD */}
                        <div
                          className={`request-card ${selected?.id === request.id ? "selected" : ""}`}
                        >
                          <div className="card-header">
                            <h3>Request ID: {request.id}</h3>
                            <button className="show-more-btn" onClick={() => setSelected(request)}>
                              Show more
                            </button>
                          </div>

                          <div className="card-body">
                            <div><strong>Support Needed:</strong><span>{request.supportNeeded}</span></div>
                            
                            <div><strong>Status:</strong><span>{getCardStatusValue(request)}</span></div>
                            <div><strong>{getCardSecondaryStatusLabel(request)}:</strong>
                              <span>{getCardSecondaryStatusValue(request)}</span>
                            </div>
                            <div><strong>Passengers:</strong><span>{getPassengerNames(request)}</span></div>
                          </div>
                        </div>

                        {/* CLOSED POPUP */}
                        {selected?.id === request.id && (
                          <RequestPopup
                            request={request}
                            popupRef={popupRef}
                            onClose={() => setSelected({})}
                            encryptId={encryptId}
                          >

                                                      
                              {request.supportNeeded.includes("Courier") ? (
                                <>
                                  <div><strong>Support Needed:</strong><span>{request.supportNeeded}</span></div>
                                  <div><strong>Courier Type:</strong><span>{request.courierType}</span></div>
                                  <div><strong>Office Location:</strong><span>{request.officeLocation}</span></div>

                                  <div className="location">
                                    <strong>Source Address:</strong>
                                    <span>{request.fromLocation || "NA"}</span>
                                  </div>

                                  <div className="location">
                                    <strong>Destination Address:</strong>
                                    <span>{request.toLocation || "NA"}</span>
                                  </div>
                                <div className="popup-field">
                                <strong>Requester Name:</strong>
                                <span>{request.user?.name || "NA"}</span>
                                 </div>
                                  <div><strong>POD Number:</strong>
                                    <span>{request.bookingDetails?.podNumber || "NA"}</span>
                                  </div>

                                  <div><strong>Received Status:</strong>
                                    <span>{request.receivedStatus ? "Yes" : "No"}</span>
                                  </div>

                                  <div><strong>Received On:</strong>
                                    <span>{request.receivedOn ? formatDate(request.receivedOn) : "NA"}</span>
                                  </div>


                                  <div><strong>Status:</strong><span>{request.status}</span></div>

                                  
                               
                                </>
                              ) : (
                                <>
                                 
                                  <div><strong>Support Needed:</strong><span>{request.supportNeeded}</span></div>
                                  <div><strong>Batch Id:</strong><span>{request.batchId}</span></div>
                                  <div ><strong>Requester Name:</strong> <span>{request.user?.name || "NA"}</span></div>
                                  <div><strong>Center:</strong><span>{request.center}</span></div>
                                  <div><strong>Client:</strong><span>{request.client}</span></div>
                                  <div><strong>Cost Center:</strong><span>{request.costCenter}</span></div>

                                  {renderJourneyDetails(request)}
                                  <div><strong>Status:</strong><span>{request.status}</span></div>
                                </>
                              )}
                          </RequestPopup>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Tabs>
          </div>

         
          {user && user.role !== "USER" && (
            <div>
              <Tabs tabs={subTabs}>

           
                <div>
                  {loading ? (
                    <p className="no-req">Loading...</p>
                  ) : allOpenRequests.length === 0 ? (
                    <p className="no-req">No Requests Found</p>
                  ) : (
                    <div className="requests-list">
                      {getVisibleRequests(allOpenRequests).map((request, index) => (
                        <div key={index}>

                          {/* CARD */}
                          <div
                            className={`request-card ${
                              selected?.id === request.id ? "selected" : ""
                            }`}
                          >
                            <div className="card-header">
                              <h3>Request ID: {request.id}</h3>
                              <button
                                className="show-more-btn"
                                onClick={() => setSelected(request)}
                              >
                                Show more
                              </button>
                            </div>

                            <div className="card-body">
                              <div>
                                <strong>Support Needed:</strong>
                                <span>{request.supportNeeded}</span>
                              </div>
                              <div>
                                <strong>Status:</strong>
                                <span>{getCardStatusValue(request)}</span>
                              </div>
                              <div>
                                <strong>{getCardSecondaryStatusLabel(request)}:</strong>
                                <span>{getCardSecondaryStatusValue(request)}</span>
                              </div>
                              <div>
                                <strong>Passengers:</strong>
                                <span>{getPassengerNames(request)}</span>
                              </div>
                            </div>
                          </div>

                          {/* POPUP */}
                        {selected?.id === request.id && (
                          <RequestPopup
                            request={request}
                            popupRef={popupRef}
                            onClose={() => setSelected({})}
                            encryptId={encryptId}
                          >
                                                        
                                {request.supportNeeded.includes("Courier") ? (
                                  <>
                                    <div>
                                      <strong>Support Needed:</strong>
                                      <span>{request.supportNeeded}</span>
                                    </div>
                                    <div>
                                      <strong>Courier Type:</strong>
                                      <span>{request.courierType}</span>
                                    </div>
                                    <div>
                                      <strong>Office Location:</strong>
                                      <span>{request.officeLocation}</span>
                                    </div>

                                    <div className="location">
                                      <strong>Source Address:</strong>
                                      <span>{request.fromLocation || "NA"}</span>
                                    </div>

                                    <div className="location">
                                      <strong>Destination Address:</strong>
                                      <span>{request.toLocation || "NA"}</span>
                                    </div>
                                       <div className="popup-field">
                                      <strong>Requester Name:</strong>
                                       <span>{request.user?.name || "NA"}</span>
                                        </div>
                                    <div>
                                      <strong>POD Number:</strong>
                                      <span>
                                        {request.bookingDetails?.podNumber || "NA"}
                                      </span>
                                    </div>

                                    <div>
                                      <strong>Status:</strong>
                                      <span>{request.status}</span>
                                    </div>

                                    {/* ACCEPT / DENY BUTTONS */}
                                    {canApproveCurrentStage(request, user) && (
                                      <div className="request-button">
                                        <button
                                          className="reqbtn"
                                          onClick={() => handleAcceptRequest(request.id)}
                                        >
                                          Approve
                                        </button>

                                        <button
                                          className="reqbtn"
                                          onClick={() => handleDenyRequest(request.id)}
                                        >
                                          Decline
                                        </button>
                                      </div>
                                    )}

                                    {/* BOOKING STATUS */}
                                    {/* BOOKING STATUS - SHOW FOR ALL COURIER REQUESTS */}
                                  <div>
                                      <strong>Booking Status:</strong>
                                      <span>{request.bookingDetails?.bookingStatus || "Pending"}</span>
                                      </div>

                                    {/* SUCCESS / FAIL BUTTONS */}
                                    {canManageBooking(request, user) && (
                                      <div className="request-button">
                                        <button
                                          className="reqbtn"
                                          onClick={() => {
                                            if (!isBookingDetailsComplete(request)) {
                                              notifyError("Update all the Booking Details", "bottom-center");
                                              return;
                                            }
                                            handleSuccess(request.id);
                                          }}
                                        >
                                          Booked
                                        </button>

                                        <button className="reqbtn" onClick={() => handleFail(request.id)}>
                                          Decline Booking
                                        </button>
                                      </div>
                                    )}

                          {/* UPDATE BOOKING DETAILS LINK */}
                                    {isAdministrationUser(user) && canManageBooking(request, user) && renderBookingDetailsAction(request)}

                                    {/* REMARKS */}
                                    <div className="remarks-container">
                                      <strong>Remarks:</strong>

                                      {Array.isArray(remarksMap[request.id]) &&
                                      remarksMap[request.id].length > 0 ? (
                                        remarksMap[request.id].map((remark, idx) => (
                                          <div key={idx} className="remark">
                                            <input
                                              type="text"
                                              value={remark}
                                              onChange={(event) =>
                                                handleChangeRemarks(
                                                  event,
                                                  request.id,
                                                  idx
                                                )
                                              }
                                            />
                                            <button
                                              className="delete-remark-button"
                                              onClick={() =>
                                                handleDeleteRemark(request.id, idx)
                                              }
                                            >
                                              <MdDelete />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <p>No remarks available</p>
                                      )}

                                      <div className="remarks-buttons-container">
                                        <button
                                          className="add-remark-button"
                                          onClick={() => handleAddRemark(request.id)}
                                        >
                                          Add Remark
                                        </button>

                                        <button
                                          className="send-remarks-button"
                                          onClick={() => sendRemarks(request.id)}
                                        >
                                          Send <IoMdSend />
                                        </button>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div>
                                      <strong>Support Needed:</strong>
                                      <span>{request.supportNeeded}</span>
                                    </div>
                                    <div ><strong>Requester Name:</strong><span>{request.user?.name || "NA"}</span></div>
                                    <div>
                                      <strong>Batch Id:</strong>
                                      <span>{request.batchId}</span>
                                    </div>
                                    <div>
                                      <strong>Center:</strong>
                                      <span>{request.center}</span>
                                    </div>
                                    <div>
                                      <strong>Client:</strong>
                                      <span>{request.client}</span>
                                    </div>
                                    <div>
                                      <strong>Cost Center:</strong>
                                      <span>{request.costCenter}</span>
                                    </div>

                                    {renderJourneyDetails(request)}

                                    <div>
                                      <strong>Status:</strong>
                                      <span>{request.status || "Pending"}</span>
                                    </div>
                                      {/* ACCEPT / DENY BUTTONS */}
                                    {canApproveCurrentStage(request, user) && (
                                      <div className="request-button">
                                        <button
                                          className="reqbtn"
                                          onClick={() => handleAcceptRequest(request.id)}
                                        >
                                          Approve
                                        </button>

                                        <button
                                          className="reqbtn"
                                          onClick={() => handleDenyRequest(request.id)}
                                        >
                                          Decline
                                        </button>
                                      </div>
                                    )}

                             {/* ✅ BOOKING STATUS */}
                              <div>
                             <strong>{getWorkflowStatusLabel(request)}:</strong>
                             <span>{getWorkflowStatusValue(request)}</span>
                               </div>

                                   
                                      {!isSelfBookedTravelAccommodation(request) && (
                                        <>
                                          {canManageBooking(request, user) && (
                                            <>
                                              <div className="request-button">
                                                <button
                                                  className="reqbtn"
                                                  onClick={() => {
                                                    if (!isBookingDetailsComplete(request)) {
                                                      notifyError("Update all the Booking Details", "bottom-center");
                                                      return;
                                                    }
                                                    handleSuccess(request.id);
                                                  }}
                                                >
                                                  Booked
                                                </button>

                                                <button
                                                  className="reqbtn"
                                                  onClick={() => handleFail(request.id)}
                                                >
                                                  Decline Booking
                                                </button>
                                              </div>

                                              {isAdministrationUser(user) && renderBookingDetailsAction(request)}
                                            </>
                                          )}
                                        </>
                                      )}
                                      <div className="remarks-container">
                                      <strong>Remarks:</strong>

                                      {Array.isArray(remarksMap[request.id]) &&
                                      remarksMap[request.id].length > 0 ? (
                                        remarksMap[request.id].map((remark, idx) => (
                                          <div key={idx} className="remark">
                                            <input
                                              type="text"
                                              value={remark}
                                              onChange={(event) =>
                                                handleChangeRemarks(
                                                  event,
                                                  request.id,
                                                  idx
                                                )
                                              }
                                            />
                                            <button
                                              className="delete-remark-button"
                                              onClick={() =>
                                                handleDeleteRemark(request.id, idx)
                                              }
                                            >
                                              <MdDelete />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <p>No remarks available</p>
                                      )}

                                      <div className="remarks-buttons-container">
                                        <button
                                          className="add-remark-button"
                                          onClick={() => handleAddRemark(request.id)}
                                        >
                                          Add Remark
                                        </button>

                                        <button
                                          className="send-remarks-button"
                                          onClick={() => sendRemarks(request.id)}
                                        >
                                          Send <IoMdSend />
                                        </button>
                                      </div>
                                    </div>
                                  </>
                                                          )}
                            </RequestPopup>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  {loading ? (
                    <p className="no-req">Loading...</p>
                  ) : allClosedRequests.length === 0 ? (
                    <p className="no-req">No Requests Found</p>
                  ) : (
                    <div className="requests-list">
                      {getVisibleRequests(allClosedRequests).map((request, index) => (
                        <div key={index}>
                          {/* CARD */}
                          <div
                            className={`request-card ${
                              selected?.id === request.id ? "selected" : ""
                            }`}
                          >
                            <div className="card-header">
                              <h3>Request ID: {request.id}</h3>
                              <button
                                className="show-more-btn"
                                onClick={() => setSelected(request)}
                              >
                                Show more
                              </button>
                            </div>

                            <div className="card-body">
                              <div>
                                <strong>Support Needed:</strong>
                                <span>{request.supportNeeded}</span>
                              </div>
                              <div>
                                <strong>Status:</strong>
                                <span>{getCardStatusValue(request)}</span>
                              </div>
                              <div>
                                <strong>{getCardSecondaryStatusLabel(request)}:</strong>
                                <span>{getCardSecondaryStatusValue(request)}</span>
                              </div>
                              <div>
                                <strong>Passengers:</strong>
                                <span>{getPassengerNames(request)}</span>
                              </div>
                            </div>
                          </div>

                          {/* POPUP */}
                        {selected?.id === request.id && (
                          <RequestPopup
                            request={request}
                            popupRef={popupRef}
                            onClose={() => setSelected({})}
                            encryptId={encryptId}
                          >
                                                        
                                {request.supportNeeded.includes("Courier") ? (
                                  <>
                                    <div>
                                      <strong>Support Needed:</strong>
                                      <span>{request.supportNeeded}</span>
                                    </div>
                                    <div>
                                      <strong>Courier Type:</strong>
                                      <span>{request.courierType}</span>
                                    </div>
                                    <div>
                                      <strong>Office Location:</strong>
                                      <span>{request.officeLocation}</span>
                                    </div>

                                    <div className="location">
                                      <strong>Source Address:</strong>
                                      <span>{request.fromLocation || "NA"}</span>
                                    </div>

                                    <div className="location">
                                      <strong>Destination Address:</strong>
                                      <span>{request.toLocation || "NA"}</span>
                                    </div>
                                    <div className="popup-field">
                                    <strong>Requester Name:</strong>
                                    <span>{request.user?.name || "NA"}</span>
                                     </div>
                                    <div>
                                      <strong>POD Number:</strong>
                                      <span>
                                        {request.bookingDetails?.podNumber || "NA"}
                                      </span>
                                    </div>

                                    <div>
                                      <strong>Received Status:</strong>
                                      <span>{request.receivedStatus ? "Yes" : "No"}</span>
                                    </div>

                                    <div>
                                      <strong>Received On:</strong>
                                      <span>
                                        {request.receivedOn
                                          ? formatDate(request.receivedOn)
                                          : "NA"}
                                      </span>
                                    </div>

                                    <div>
                                      <strong>Status:</strong>
                                      <span>{request.status}</span>
                                    </div>
                                  </>
                                ) : (
                                  <>

                                    <div>
                                      <strong>Support Needed:</strong>
                                      <span>{request.supportNeeded}</span>
                                    </div>
                                    <div >
                                     <strong>Requester Name:</strong>
                                     <span>{request.user?.name || "NA"}</span>
                                      </div>

                                    <div>
                                      <strong>Batch Id:</strong>
                                      <span>{request.batchId}</span>
                                    </div>

                                    <div>
                                      <strong>Center:</strong>
                                      <span>{request.center}</span>
                                    </div>
                                    <div>
                                      <strong>Client:</strong>
                                      <span>{request.client}</span>
                                    </div>
                                    <div>
                                      <strong>Cost Center:</strong>
                                      <span>{request.costCenter}</span>
                                    </div>

                                    {renderJourneyDetails(request)}

                                    <div>
                                      <strong>Status:</strong>
                                      <span>{request.status}</span>
                                    </div>

                                    <div>
                                      <strong>{getWorkflowStatusLabel(request)}:</strong>
                                      <span>{getWorkflowStatusValue(request)}</span>
                                    </div>

                                            {!isSelfBookedTravelAccommodation(request) && isAdministrationUser(user) && renderBookingDetailsAction(request)}

                                    {/* REMARKS */}
                                    <div className="remarks-container">
                                      <strong>Remarks:</strong>

                                      {Array.isArray(remarksMap[request.id]) &&
                                      remarksMap[request.id].length > 0 ? (
                                        remarksMap[request.id].map((remark, idx) => (
                                          <div key={idx} className="remark">
                                            <input
                                              type="text"
                                              value={remark}
                                              onChange={(event) =>
                                                handleChangeRemarks(
                                                  event,
                                                  request.id,
                                                  idx
                                                )
                                              }
                                            />
                                            <button
                                              className="delete-remark-button"
                                              onClick={() =>
                                                handleDeleteRemark(request.id, idx)
                                              }
                                            >
                                              <MdDelete />
                                            </button>
                                          </div>
                                        ))
                                      ) : (
                                        <p>No remarks available</p>
                                      )}

                                      

                                      <div className="remarks-buttons-container">
                                        <button
                                          className="add-remark-button"
                                          onClick={() => handleAddRemark(request.id)}
                                        >
                                          Add Remark
                                        </button>

                                        <button
                                          className="send-remarks-button"
                                          onClick={() => sendRemarks(request.id)}
                                        >
                                          Send <IoMdSend />
                                        </button>
                                      </div>
                                    </div>
                                    
                                  </>
                                )}
                            </RequestPopup>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Tabs>
            </div>
            
          )}
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default ViewEditRequests;